// frontend/bookaroom/react-functions/src/BaroomApp.js
import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import LoginScreen from './components/LoginScreen';
import ErrorBoundary from './components/shared/ErrorBoundary';
import OfflineIndicator from './components/shared/OfflineIndicator';
import i18n from './i18n';
import api from './services/api';
import { saveRememberToken, getRememberToken, clearRememberToken } from './services/authStorage';

// Lazy-load dashboards – each role only downloads its own bundle
const UserDashboard = lazy(() => import('./components/UserDashboard'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const OwnerDashboard = lazy(() => import('./components/OwnerDashboard'));
// Kiosk board – its own bundle, only fetched on staff devices using ?view=kiosk
const Kiosk = lazy(() => import('./components/Kiosk'));
// Stripe redirect-return handler – its own bundle, only fetched when the user
// comes back from a redirect payment method (?payment_return=1 / Stripe's
// payment_intent_client_secret param).
const PaymentReturn = lazy(() => import('./components/user/PaymentReturn'));
// Public (Calendly-style) booking page – its own bundle, only fetched when the
// boot URL carries a "?publicBook=<token>". Standalone, NO account/dashboard.
const PublicBooking = lazy(() => import('./components/PublicBooking'));

function BaroomApp() {
  // react-i18next: `t` for translated a11y strings and `i18n.language` so the
  // <html lang> sync effect below re-runs whenever the language changes.
  const { t, i18n: i18nInstance } = useTranslation();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isAutoLogging, setIsAutoLogging] = useState(true);

  // Public (Calendly-style) booking token from "?publicBook=<token>". Captured
  // SYNCHRONOUSLY on first render (lazy initializer) so the standalone public
  // page renders immediately — before the login screen / auto-login spinner —
  // for an anonymous visitor following a shared link. The query string is
  // stripped in the boot effect below (same as the other boot params).
  const [publicBookToken] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('publicBook') || null;
    } catch {
      return null;
    }
  });

  // Ref to the <main> landmark so the authenticated view can best-effort move
  // focus to it on first mount (helps keyboard/screen-reader users).
  const mainRef = useRef(null);

  // Deep-link from a scanned room QR ("?room=<id>&intent=book|view").
  // Parsed once on boot and held in a ref so it survives the auto-login flow
  // and any re-renders. Consumed exactly once when the user dashboard mounts.
  const deepLinkRef = useRef(null);

  // Kiosk board flag from "?view=kiosk" on a staff device. Parsed once on boot
  // and held in a ref (same pattern as the deep-link) so it survives the
  // auto-login flow and any re-renders. Consumed once after login so a later
  // logout returns to the normal LoginScreen rather than re-entering the kiosk.
  const kioskViewRef = useRef(false);

  // Stripe redirect-return flag from "?payment_return=1" (or Stripe's own
  // "payment_intent_client_secret" param it appends to the return_url). Parsed
  // once on boot and held in a ref (same pattern as the deep-link / kiosk flags)
  // so it survives the auto-login flow. Consumed once after login to render
  // <PaymentReturn /> instead of the dashboard; a not-logged-in user sees
  // LoginScreen first and the return is consumed after login.
  const paymentReturnRef = useRef(false);

  // Bumped to force a re-render once the user dismisses <PaymentReturn /> (the
  // flag is a ref, so we need a state change to fall through to the dashboard).
  const [paymentReturnDone, setPaymentReturnDone] = useState(false);

  // Parse boot-time URL params (room deep-link + kiosk view + Stripe return),
  // then strip the query string so a refresh (or back/forward) won't re-trigger
  // them.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const roomId = params.get('room');
      const isKiosk = params.get('view') === 'kiosk';
      // A return from a redirect payment method. Stripe redirects to our
      // return_url (?payment_return=1) and also appends its own
      // payment_intent_client_secret param — detect either.
      const isPaymentReturn =
        params.get('payment_return') === '1' ||
        params.has('payment_intent_client_secret');

      if (roomId) {
        const rawIntent = params.get('intent');
        const intent = rawIntent === 'book' ? 'book' : 'view';
        deepLinkRef.current = { roomId, intent };
      }

      if (isKiosk) {
        kioskViewRef.current = true;
      }

      if (isPaymentReturn) {
        paymentReturnRef.current = true;
      }

      // Public booking token is captured synchronously in state above; here we
      // only need to know whether to strip the query string.
      const hasPublicBook = !!params.get('publicBook');

      // Clear the query string while keeping the current path + hash, but only
      // if we actually consumed a recognized param (preserves prior behavior).
      if (roomId || isKiosk || isPaymentReturn || hasPublicBook) {
        const cleanUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } catch (err) {
      // URL parsing / history API should never break app boot.
      console.error('Failed to parse boot URL params:', err);
    }
  }, []);

  // Auto-login with stored remember token on app startup
  useEffect(() => {
    const attemptAutoLogin = async () => {
      // A public booking link is a standalone, no-account page — skip auto-login
      // entirely so an anonymous visitor goes straight to <PublicBooking />.
      if (publicBookToken) {
        setIsAutoLogging(false);
        return;
      }
      const token = getRememberToken();
      if (!token) {
        setIsAutoLogging(false);
        return;
      }

      try {
        const data = await api.tokenAuth(token);
        // Save the rotated token
        if (data.rememberToken) {
          saveRememberToken(data.rememberToken);
        }
        // Sync language preference to backend (fire-and-forget)
        api.saveLanguage(i18n.language || 'en').catch(() => {});
        setIsLoggedIn(true);
        setUserData(data);
      } catch (err) {
        console.error('Auto-login failed:', err);
        clearRememberToken();
      } finally {
        setIsAutoLogging(false);
      }
    };

    attemptAutoLogin();
  }, []);

  // Keep <html lang> in sync with the active UI language so screen readers
  // announce content in the right language (WCAG 3.1.1 / 3.1.2). Runs on mount
  // and whenever the language changes. Best-effort only — never breaks boot.
  useEffect(() => {
    try {
      const lang = (i18nInstance.language || 'en').split('-')[0];
      document.documentElement.lang = lang;
    } catch {
      // document access should never break the app.
    }
  }, [i18nInstance.language]);

  // When the authenticated view first mounts, best-effort move focus to the
  // <main> landmark. Deferred a tick so it doesn't fight the existing
  // deep-link / kiosk focus handling inside the dashboards.
  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const id = window.setTimeout(() => {
      try {
        if (mainRef.current) {
          mainRef.current.focus({ preventScroll: true });
        }
      } catch {
        // focus() is best-effort; ignore any failure.
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, [isLoggedIn]);

  const handleLoginSuccess = (data) => {
    // Save remember token for auto-login on next app start
    if (data.rememberToken) {
      saveRememberToken(data.rememberToken);
    }

    // Sync language preference to backend (fire-and-forget)
    api.saveLanguage(i18n.language || 'en').catch(() => {});
    setIsLoggedIn(true);
    setUserData(data);
  };

  // Admin "Login as User" – switch session to the target user
  const handleSwitchUser = (data) => {
    // Clear the admin's remember token
    clearRememberToken();
    // Save the user's remember token if provided (won't exist if SMS was required)
    if (data.rememberToken) {
      saveRememberToken(data.rememberToken);
    }
    // Switch the app to the user's dashboard
    setUserData(data);
  };

  const handleLogout = async () => {
    // Clear stored token so auto-login won't happen
    clearRememberToken();

    try {
      await api.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggedIn(false);
      setUserData(null);
      sessionStorage.clear();
    }
  };

  // Suspense fallback – spinner shown while lazy-loading dashboard bundles
  const dashboardFallback = (
    <div style={{
      height: '100vh',
      width: '100vw',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#ffffff'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid #e0e0e0',
        borderTopColor: '#3B82F6',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const renderDashboard = () => {
    let dashboard;
    if (userData?.isOwner) {
      dashboard = <OwnerDashboard userData={userData} onLogout={handleLogout} />;
    } else if (userData?.isAdmin) {
      dashboard = <AdminDashboard userData={userData} onLogout={handleLogout} onSwitchUser={handleSwitchUser} />;
    } else {
      // Hand any room deep-link to the user dashboard. Consume it once so a
      // later re-render (e.g. language switch) doesn't re-trigger the redirect.
      // Admins/owners are intentionally excluded — they keep their dashboards.
      const deepLink = deepLinkRef.current;
      deepLinkRef.current = null;
      dashboard = (
        <UserDashboard
          userData={userData}
          onLogout={handleLogout}
          initialRoomId={deepLink ? deepLink.roomId : null}
          initialIntent={deepLink ? deepLink.intent : null}
        />
      );
    }
    return <Suspense fallback={dashboardFallback}>{dashboard}</Suspense>;
  };

  // Render the Stripe redirect-return handler. Shown once after the user comes
  // back from a redirect payment method; <PaymentReturn /> polls the
  // webhook-written status and, on dismiss, calls onDone to fall through to the
  // normal dashboard. The flag is consumed by the caller so a refresh/logout
  // doesn't re-enter it.
  const handlePaymentReturnDone = () => {
    // Flag is already consumed (set false) in renderAuthenticated; setting this
    // forces a re-render so we fall through to the dashboard, and marks the
    // return as handled so it can never be re-shown this session.
    setPaymentReturnDone(true);
  };

  const renderPaymentReturn = () => (
    <Suspense fallback={dashboardFallback}>
      <PaymentReturn onDone={handlePaymentReturnDone} />
    </Suspense>
  );

  // Render the kiosk board. Reuses the existing authenticated session — the
  // board's reads (getRooms / searchAvailability / getSpaces) are session-
  // scoped, so there is no public/no-auth path. The flag is consumed once by
  // the caller so a later logout returns to the normal LoginScreen.
  const renderKiosk = () => (
    <Suspense fallback={dashboardFallback}>
      <Kiosk />
    </Suspense>
  );

  // A public (Calendly-style) booking link takes precedence over everything:
  // it is a standalone, no-account page rendered for an anonymous visitor who
  // followed a shared link. No login screen, no dashboard, no auto-login.
  if (publicBookToken) {
    return (
      <ErrorBoundary>
        <Suspense fallback={dashboardFallback}>
          <PublicBooking token={publicBookToken} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // Show spinner while checking for stored token (prevents login screen flash)
  if (isAutoLogging) {
    return (
      <div style={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #e0e0e0',
          borderTopColor: '#3B82F6',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Decide what the authenticated session should render. If the boot URL asked
  // for the kiosk board, consume the flag once and show <Kiosk /> instead of
  // the role dashboard. Not-logged-in users still see LoginScreen first; the
  // flag survives in the ref until login completes, then is consumed here.
  const renderAuthenticated = () => {
    // A Stripe redirect return takes precedence: show the one-time return
    // handler, then fall through to the dashboard once dismissed. Consume the
    // flag here so a later re-render/logout won't re-enter it. `paymentReturnDone`
    // guards against re-entry: once the user has dismissed the return view we
    // never show it again for this session, even if the flag were somehow set.
    if (paymentReturnRef.current && !paymentReturnDone) {
      paymentReturnRef.current = false;
      return renderPaymentReturn();
    }
    if (kioskViewRef.current) {
      kioskViewRef.current = false;
      return renderKiosk();
    }
    return renderDashboard();
  };

  return (
    <ErrorBoundary>
      {/* Skip link — first focusable element so keyboard users can bypass any
          chrome and jump straight to the main landmark (WCAG 2.4.1). */}
      <a href="#main-content" className="skip-link">
        {t('a11y.skipToContent')}
      </a>
      <OfflineIndicator />
      <main
        id="main-content"
        ref={mainRef}
        tabIndex={-1}
        style={{ height: '100vh', width: '100vw' }}
      >
        {!isLoggedIn ? (
          <LoginScreen onLoginSuccess={handleLoginSuccess} />
        ) : (
          renderAuthenticated()
        )}
      </main>
    </ErrorBoundary>
  );
}

export default BaroomApp;
